import { useWorkingTimeStore } from '@nsftx/seven-client-retail-vue-dm'
import {
  betslipService,
  bettingAcceptanceErrorParser,
  getBetPayoutAmount,
  useBetslip,
} from '@nsftx/seven-client-retail-vue-sgp'
import type { Bet } from '@nsftx/seven-client-retail-vue-integrator'
import { logService } from '@nsftx/seven-client-retail-utils'
import type { PrintContextPayoutSlip } from '@nsftx/seven-client-retail-print'
import i18n from '@/plugins/translationsPlugin'
import { SlipPayoutHooksManager } from './slipPayoutHooksManager'
import SlipPayoutValidationError from './SlipPayoutValidationError'
import errorParser from '@/utils/errorParser'
import printService from '../print/services/printService'
import { useNotificationsStore } from '@nsftx/seven-nxg-retail-vue-sdk'
import { NXGDSNotificationVariants } from '@nsftx/seven-nxg-design-system'
import { useSlipCheckStore } from '@/stores/slip-check/slipCheckStore'
import { recordBetslipPayoutTransaction } from '@/modules/calculator/calculatorTransactionService'

const LOG_PREFIX = '[slipPayoutService]'
const { t } = i18n.global

const validateSlipPayout = () => SlipPayoutHooksManager.callHook('BeforeSlipPayout')

const slipPayout = async (betslipId: string, bet: Bet) => {
  const { showNotification } = useNotificationsStore()
  const { closeSlipCheckDetailsModal } = useSlipCheckStore()
  const { betslip, betslipCurrency, betslipProductId } = useBetslip()

  const productId = betslipProductId.value
  const betIds = [bet.id]
  const payoutAmount = getBetPayoutAmount(bet)

  try {
    logService.info(`${LOG_PREFIX} Sending slip to Betslip API`, {
      code: 'SS_SLIP_PAYOUT_BETSLIP_REQUEST',
      ticket_code: betslipId,
      product_displayid: productId,
      bets: betIds,
    })
    await betslipService.payoutTicket(betslipId, betIds)
    logService.info(`${LOG_PREFIX} Slip paid out via Betslip API`, {
      code: 'SS_SLIP_PAYOUT_BETSLIP_SUCCESS',
      ticket_code: betslipId,
      product_displayid: productId,
    })

    const printAddJobOptions: PrintContextPayoutSlip = {
      meta: {
        productDisplayId: productId?.toString() || '',
        requestId: betslipId,
      },
      model: {
        slip: betslip.value!,
      },
    }

    recordBetslipPayoutTransaction(
      betslipId,
      bet,
      betslipCurrency.value || 'EUR',
      productId?.toString() || '',
    )

    await printService
      .getPrintService()
      .performPrint(`games${productId}`, 'betslip_payout', printAddJobOptions)

    closeSlipCheckDetailsModal()
    showNotification({
      variant: NXGDSNotificationVariants.success,
      text: [
        t('ticket_payout_success', {
          amount: payoutAmount,
          currency: betslipCurrency.value,
        }),
      ],
    })
  } catch (error) {
    logService.error(`${LOG_PREFIX} Failed to payout slip`, {
      code: 'SS_SLIP_PAYOUT_ERROR',
      ticket_code: betslipId,
      product_displayid: productId,
      bets: betIds,
      ...bettingAcceptanceErrorParser.parseUpstream(error),
    })

    showNotification({
      variant: NXGDSNotificationVariants.error,
      text: [
        t('ticket_payout_error', {
          amount: payoutAmount,
          currency: betslipCurrency.value,
        }),
      ],
    })
  }
}

const handleSlipPayoutValidationError = (error: unknown) => {
  const { showNotification } = useNotificationsStore()
  const errorMessage =
    error instanceof SlipPayoutValidationError ? error.message : t('common_error')

  logService.error(`${LOG_PREFIX} Slip payout validation has failed`, {
    code: 'SS_SLIP_PAYOUT_VALIDATION_ERROR',
    ...errorParser.parseUpstream(error),
  })
  showNotification({
    variant: NXGDSNotificationVariants.error,
    text: [errorMessage],
  })
}

const handleSlipPayout = async (betslipId: string, bet: Bet) => {
  try {
    await validateSlipPayout()
    await slipPayout(betslipId, bet)
  } catch (error) {
    handleSlipPayoutValidationError(error)
  }
}

const registerPayoutHooks = () => {
  SlipPayoutHooksManager.hook('BeforeSlipPayout', () => {
    const workingTimeStore = useWorkingTimeStore()
    const { isValid, nextValidTime } = workingTimeStore.isWorkingTimeValid()

    if (!isValid) {
      const errorMessage = t('working_time_over', {
        time: nextValidTime || t('common_unknown'),
      })

      return Promise.reject(
        new SlipPayoutValidationError(errorMessage, 'SS_SLIP_PAYOUT_VALIDATION_WORKING_TIME_ERROR'),
      )
    }

    return Promise.resolve()
  })
}

export { registerPayoutHooks, handleSlipPayout }
